/* ----------------- React --------------- */
import { useEffect, useRef, useState } from "react";

/* ----------------- React Native --------------- */
import { type ScrollView, SectionList } from "react-native";

/* ----------------- Types --------------- */
import type { HeaderSectionData, HomeSection, SectionData } from "../../types/";

/* ----------------- Hooks --------------- */
import { useDevice } from "@hooks/useDevice";

/* ----------------- UI Components --------------- */
import { ExamsFilterDialog } from "components/ExamsFilterDialog";

import ExamInfoSection from "./ExamInfoSection";
import ProductsSection from "./ProductsSection";
/* ----------------- Child Components --------------- */
import { SectionHeader } from "./SectionHeader";
import TasksSection from "./TasksSection";
import TopicsSection from "./TopicsSection";

import { useExamContext } from "@features/exam/contexts/ExamContext";
/* ----------------- Constants and Helpers --------------- */
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import type { PracticeTestExam } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { TextSkeleton } from "@memorang/ui/components/ChatUI/TextSkeleton";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { PurchaseDialog } from "../PurchaseDialog";
type Props = {
	sections: HomeSection[];
	headerData?: HeaderSectionData;
	handleSelectTopic: (topicId: number) => void;
	handleShowPurchase: () => void;
	studyPackSubtitle?: string;
	showPurchaseDialog?: boolean;
	toggleShowPurchaseDialog?: () => void;
	hasAccess?: boolean;
	exams?: PracticeTestExam[];
};

const renderSectionHeader = ({
	section: { title, chipProps, loading },
	handleShowPurchase,
	isMobile,
}: {
	section: HomeSection;
	handleShowPurchase?: () => void;
	isMobile: boolean;
}) => {
	return loading ? (
		<Box paddingVertical={16} paddingHorizontal={isMobile ? 16 : 0} gap={16}>
			<Box width={200}>
				<TextSkeleton numLines={1} />
			</Box>
		</Box>
	) : (
		<SectionHeader
			sectionTitle={title}
			chipProps={chipProps}
			handleClickChips={handleShowPurchase}
		/>
	);
};

export const HomeView = ({
	sections,
	headerData,
	handleSelectTopic,
	handleShowPurchase,
	studyPackSubtitle,
	showPurchaseDialog,
	toggleShowPurchaseDialog,
	hasAccess,
	exams,
}: Props) => {
	const { isMobile } = useDevice();
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const scrollContainerRef = useRef<ScrollView>(null);

	useEffect(() => {
		scrollContainerRef.current?.scrollTo({ y: 0, animated: true });
	}, []);

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);
	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};

	const renderItem = ({
		item,
		section,
	}: {
		item: SectionData;
		section: {
			onPress?: () => void;
			loading?: boolean;
		};
	}) => {
		const { type, items } = item;

		if (type === "tasks") {
			return <TasksSection items={items} loading={section.loading} />;
		}
		if (type === "progress") {
			return (
				<TopicsSection
					items={items}
					showStudyPackViews={showStudyPackViews}
					handleSelectTopic={handleSelectTopic}
					handleShowPurchase={handleShowPurchase}
					studyPackSubtitle={studyPackSubtitle}
					handleNavigateToDetailView={section.onPress}
					loading={section.loading}
				/>
			);
		}

		if (type === "exam") {
			return <ProductsSection items={items} loading={section.loading} />;
		}
		return null;
	};

	return (
		<ScrollContainer
			ref={scrollContainerRef}
			contentContainerStyle={{
				flexGrow: 1,
				paddingBottom: 84,
				paddingTop: isMobile ? 0 : 16,
				paddingHorizontal: isMobile ? 0 : 16,
			}}
			maxWidth="sm"
			accessibilityLabel={`This is the main home content, featuring the Exam Information section, Recommended Tasks, ${practiceTestLabel ?? "Practice Tests"}, and Mastery Items for your review.`}
			accessible
			// biome-ignore lint/a11y/useSemanticElements: <explanation>
			role="main"
		>
			{sections.length ? (
				<SectionList
					sections={sections}
					scrollEnabled={false}
					showsVerticalScrollIndicator={false}
					ListHeaderComponent={
						<ExamInfoSection
							headerData={headerData}
							hasAccess={hasAccess}
							handleShowPurchase={handleShowPurchase}
							exams={exams}
						/>
					}
					// SectionSeparatorComponent={() => <Box height={4} />}
					renderItem={({ item, section }) => {
						return renderItem({
							item,
							section,
						});
					}}
					contentContainerStyle={{
						padding: isMobile ? 0 : 8,
					}}
					renderSectionHeader={({ section }) => {
						return renderSectionHeader({
							section,
							handleShowPurchase: hasAccess ? undefined : handleShowPurchase,
							isMobile,
						});
					}}
				/>
			) : null}

			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog!}
					productType="PRACTICE_TEST"
					fromHomePage
				/>
			)}
		</ScrollContainer>
	);
};
